import React, { useState, useEffect } from 'react';
import { FaFileAlt, FaClock, FaCheckCircle, FaTimesCircle, FaSpinner, FaFilePdf, FaFileWord, FaTrash, FaUser, FaEdit, FaPlus, FaFileUpload, FaTimes, FaUserTie, FaCalendarAlt, FaHourglassHalf, FaFileContract, FaEye, FaUserCheck, FaSearch, FaChevronDown, FaCheck } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Toast from '../components/Toast';
import * as authService from '../services/authService';
import * as articleService from '../services/articleService';
import * as masterService from '../services/masterService';

const MyAbstract = () => {
  const [abstracts, setAbstracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [membersMap, setMembersMap] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [abstractToDelete, setAbstractToDelete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [abstractToEdit, setAbstractToEdit] = useState(null);
  const [editUploadedFile, setEditUploadedFile] = useState(null);
  const [toast, setToast] = useState(null);
  const [showReviewCommentModal, setShowReviewCommentModal] = useState(false);
  const [selectedReviewComment, setSelectedReviewComment] = useState('');
  const [reviewCommentType, setReviewCommentType] = useState(''); // 'accepted' or 'rejected'
  const navigate = useNavigate();

  // Master data from API
  const [masterSubjects, setMasterSubjects] = useState([]);
  const [masterStreams, setMasterStreams] = useState([]);
  const [masterSpecializations, setMasterSpecializations] = useState([]);
  const [universityList, setUniversityList] = useState([]);
  const [masterUniversities, setMasterUniversities] = useState([]);
  const [allMembers, setAllMembers] = useState({ reviewers: [], editorial_board: [], sub_eb_members: [] });

  // Search states for edit modal searchable dropdowns
  const [searchUniversity, setSearchUniversity] = useState('');
  const [showUniversityDropdown, setShowUniversityDropdown] = useState(false);
  const [searchMentorUniversity, setSearchMentorUniversity] = useState('');
  const [showMentorUniversityDropdown, setShowMentorUniversityDropdown] = useState(false);

  // Reviewer multi-select states for edit modal
  const [selectedReviewers, setSelectedReviewers] = useState([]);
  const [reviewerSearch, setReviewerSearch] = useState('');
  const [showReviewerDropdown, setShowReviewerDropdown] = useState(false);
  const reviewerDropdownRef = React.useRef(null);
  const reviewerInputRef = React.useRef(null);

  // Filtered data for cascading dropdowns in edit modal
  const [filteredStreams, setFilteredStreams] = useState([]);
  const [filteredSpecializations, setFilteredSpecializations] = useState([]);

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser) {
      navigate('/login');
      return;
    }

    loadAbstracts();
    loadMembers();
    loadMasterData();
  }, [navigate]);

  // Outside click listener for reviewer dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (reviewerDropdownRef.current && !reviewerDropdownRef.current.contains(event.target)) {
        setShowReviewerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadMasterData = async () => {
    try {
      const [subjectsRes, streamsRes, specsRes, univRes] = await Promise.all([
        masterService.getAllSubjects(),
        masterService.getAllStreams(),
        masterService.getAllSpecializations(),
        masterService.getAllUniversities()
      ]);

      setMasterSubjects(Array.isArray(subjectsRes) ? subjectsRes : (subjectsRes?.data || []));
      setMasterStreams(Array.isArray(streamsRes) ? streamsRes : (streamsRes?.data || []));
      setMasterSpecializations(Array.isArray(specsRes) ? specsRes : (specsRes?.data || []));

      if (univRes.success && Array.isArray(univRes.data)) {
        const topUnivs = univRes.data.slice(0, 50);
        setUniversityList(topUnivs);
        setMasterUniversities(topUnivs);
      } else if (Array.isArray(univRes)) {
        const topUnivs = univRes.slice(0, 50);
        setUniversityList(topUnivs);
        setMasterUniversities(topUnivs);
      }
    } catch (error) {
      console.error('Error loading master data:', error);
    }
  };

  // Debounced university search for edit modal
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchUniversity && searchUniversity.length >= 2) {
        if (abstractToEdit?.university === searchUniversity && universityList.length > 0) return;
        try {
          const response = await masterService.searchUniversities(searchUniversity);
          let results = [];
          if (response.success && Array.isArray(response.data)) {
            results = response.data;
          } else if (Array.isArray(response)) {
            results = response;
          }
          setUniversityList(results);
          setShowUniversityDropdown(true);
        } catch (error) {
          console.error('Error searching universities:', error);
        }
      } else {
        setUniversityList(masterUniversities);
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchUniversity, abstractToEdit?.university]);

  // Debounced mentor university search for edit modal
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchMentorUniversity && searchMentorUniversity.length >= 2) {
        const currentMentorUniv = abstractToEdit?.mentorUniversity || abstractToEdit?.guideUniversity;
        if (currentMentorUniv === searchMentorUniversity && universityList.length > 0) return;
        try {
          const response = await masterService.searchUniversities(searchMentorUniversity);
          let results = [];
          if (response.success && Array.isArray(response.data)) {
            results = response.data;
          } else if (Array.isArray(response)) {
            results = response;
          }
          setUniversityList(results);
          setShowMentorUniversityDropdown(true);
        } catch (error) {
          console.error('Error searching mentor universities:', error);
        }
      } else {
        setUniversityList(masterUniversities);
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchMentorUniversity, abstractToEdit?.mentorUniversity, abstractToEdit?.guideUniversity]);

  const loadMembers = async () => {
    try {
      const response = await masterService.getMembers();
      const data = response.data || response;
      const map = {};

      const reviewers = Array.isArray(data.reviewers) ? data.reviewers : [];
      const eb = Array.isArray(data.editorial_board) || Array.isArray(data.editorialBoard)
        ? (data.editorial_board || data.editorialBoard) : [];
      const subEb = Array.isArray(data.sub_eb_members) || Array.isArray(data.subEB)
        ? (data.sub_eb_members || data.subEB) : [];

      [...reviewers, ...eb, ...subEb].forEach(m => {
        if (m.id) {
          map[m.id] = m.full_name || m.name || m.user?.full_name || m.user?.name;
        }
      });
      setMembersMap(map);
      setAllMembers({
        reviewers: reviewers,
        editorial_board: eb,
        sub_eb_members: subEb
      });
    } catch (error) {
      console.error('Error loading members for lookup:', error);
    }
  };

  const loadAbstracts = async () => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser) return;

    setLoading(true);
    try {
      const response = await articleService.getMyAbstracts();
      console.log('My Abstracts API response:', response);

      // Extract abstracts array from response
      let abstractsData = [];
      if (response?.success && Array.isArray(response.data)) {
        abstractsData = response.data;
      } else if (Array.isArray(response)) {
        abstractsData = response;
      } else if (response?.data && Array.isArray(response.data)) {
        abstractsData = response.data;
      }

      // Map API fields to component-expected fields
      const mappedAbstracts = abstractsData.map(item => ({
        id: item.id || item._id,
        abstractTitle: item.title || item.abstractTitle || '',
        subject: item.subject?.name || item.subject_name || item.subject || '',
        subject_id: item.subject_id || item.subject?.id || '',
        major: item.stream?.name || item.stream_name || item.major || '',
        stream_id: item.stream_id || item.stream?.id || '',
        specialization: item.specialization?.name || item.specialization_name || item.specialization || '',
        specialization_id: item.specialization_id || item.specialization?.id || '',
        university: item.university || '',
        keywords: item.keywords || '',
        abstractText: item.abstract_text || item.abstractText || '',
        contentType: item.contentType || (item.file_url ? 'file' : 'text'),
        uploadedFile: (() => {
          const fileUrl = item.file_url || item.versions?.[0]?.file_url || null;
          return fileUrl ? { name: fileUrl.split('/').pop(), url: fileUrl } : null;
        })(),
        authors: item.co_authors || item.authors || [],
        mentorName: item.guide_details?.name || item.mentorName || item.guideName || '',
        mentorDesignation: item.guide_details?.designation || item.mentorDesignation || item.guideDesignation || '',
        mentorUniversity: item.guide_details?.university || item.mentorUniversity || item.guideUniversity || '',
        mentorPhone: item.guide_details?.phone || item.mentorPhone || item.guidePhone || '',
        mentorEmail: item.guide_details?.email || item.mentorEmail || item.guideEmail || '',
        status: (item.status || 'submitted').toLowerCase(),
        submittedAt: item.created_at || item.submittedAt || item.createdAt || '',
        inQueueAt: item.in_queue_at || item.inQueueAt || '',
        inReviewAt: item.in_review_at || item.inReviewAt || '',
        // Final decision dates
        acceptedAt: item.accepted_at || item.review_assignments?.find(ra => ra.decision === 'ACCEPT')?.review_date || '',
        rejectedAt: item.rejected_at || item.review_assignments?.find(ra => ra.decision === 'REJECT')?.review_date || '',
        modificationRequestedAt: item.review_assignments?.find(ra => ra.decision === 'MODIFY')?.review_date || '',

        reviewComment: item.review_comment || item.reviewComment || item.review_assignments?.find(ra => ra.comments)?.comments || '',
        assignedTo: item.assigned_reviewer || item.assigned_reviewer_id || item.reviewer || item.assigned_to || item.assignedTo || null,
        submissionDate: (item.submission_date || item.submissionDate) ? new Date(item.submission_date || item.submissionDate).toISOString().split('T')[0] : '',
        selectedReviewers: (() => {
          const raw = item.suggested_reviewers || item.suggested_reviewer_details || item.selectedReviewers || item.reviewers || item.suggested_reviewer_ids || [];
          if (typeof raw === 'string') {
            try {
              return JSON.parse(raw);
            } catch (e) {
              return [raw];
            }
          }
          return Array.isArray(raw) ? raw : [];
        })(),
        assignedReviewerDate: (() => {
          const validAssignment = item.review_assignments?.find(ra => ra.status !== 'REJECTED' || ra.decision);
          return validAssignment?.created_at || '';
        })(),
        reviewerResponseDate: (() => {
          const validAssignment = item.review_assignments?.find(ra => ra.status !== 'REJECTED' || ra.decision);
          return (validAssignment?.status !== 'PENDING' && validAssignment?.response_date) ? validAssignment.response_date : '';
        })(),
        isReviewerInvitationRejected: item.review_assignments?.[0]?.status === 'REJECTED' && !item.review_assignments?.[0]?.decision,
      }));

      // Sort by submission date in descending order (newest first)
      const sortedAbstracts = mappedAbstracts.sort((a, b) => {
        const dateA = new Date(a.submittedAt);
        const dateB = new Date(b.submittedAt);
        return dateB - dateA;
      });

      setAbstracts(sortedAbstracts);
    } catch (error) {
      console.error('Error loading abstracts from API:', error);
      showToast('Failed to load abstracts. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const getStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case 'submitted':
        return {
          label: 'Submitted',
          bgColor: 'bg-blue-100',
          textColor: 'text-blue-700',
          icon: FaClock,
          borderColor: 'border-blue-500'
        };
      case 'in_queue':
      case 'in-queue':
      case 'pending':
        return {
          label: 'In Queue',
          bgColor: 'bg-orange-100',
          textColor: 'text-orange-700',
          icon: FaHourglassHalf,
          borderColor: 'border-orange-500'
        };
      case 'in_review':
      case 'in-review':
      case 'under_review':
        return {
          label: 'In Review',
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-700',
          icon: FaSpinner,
          borderColor: 'border-yellow-500'
        };
      case 'assigned':
      case 'reviewer_assigned':
        return {
          label: 'Reviewer Assigned',
          bgColor: 'bg-indigo-100',
          textColor: 'text-indigo-700',
          icon: FaUserCheck,
          borderColor: 'border-indigo-500'
        };
      case 'accepted':
        return {
          label: 'Accepted',
          bgColor: 'bg-green-100',
          textColor: 'text-green-700',
          icon: FaCheckCircle,
          borderColor: 'border-green-500'
        };
      case 'rejected':
        return {
          label: 'Rejected',
          bgColor: 'bg-red-100',
          textColor: 'text-red-700',
          icon: FaTimesCircle,
          borderColor: 'border-red-500'
        };
      case 'modification_requested':
      case 'modification-requested':
        return {
          label: 'Modification Requested',
          bgColor: 'bg-amber-100',
          textColor: 'text-amber-700',
          icon: FaEdit,
          borderColor: 'border-amber-500'
        };
      case 'resubmitted':
        return {
          label: 'Resubmitted',
          bgColor: 'bg-teal-100',
          textColor: 'text-teal-700',
          icon: FaClock,
          borderColor: 'border-teal-500'
        };
      default:
        return {
          label: 'Unknown',
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-700',
          icon: FaClock,
          borderColor: 'border-gray-500'
        };
    }
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return null;
    const extension = fileName.split('.').pop().toLowerCase();
    if (extension === 'pdf') {
      return <FaFilePdf className="text-red-500 text-xl" />;
    } else if (extension === 'doc' || extension === 'docx') {
      return <FaFileWord className="text-blue-500 text-xl" />;
    }
    return <FaFileAlt className="text-gray-500 text-xl" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleReviewCommentClick = (abstract) => {
    if (abstract.reviewComment && abstract.reviewComment.trim()) {
      setSelectedReviewComment(abstract.reviewComment);
      setReviewCommentType(abstract.status); // 'accepted' or 'rejected'
      setShowReviewCommentModal(true);
    }
  };

  const handleEditClick = (abstract) => {
    // Add unique IDs and temporary search/dropdown states to each author for the edit modal
    const authorsWithStates = (abstract.authors || []).map((a, idx) => ({
      ...a,
      id: a.id || `author-${Date.now()}-${idx}`,
      searchUniversity: a.university || '',
      showUniversityDropdown: false
    }));

    setAbstractToEdit({ ...abstract, authors: authorsWithStates });
    setEditUploadedFile(abstract.uploadedFile || null);
    setSearchUniversity(abstract.university || '');
    setSearchMentorUniversity(abstract.mentorUniversity || '');

    // Set initial filtered streams/specs for the modal
    if (abstract.subject_id) {
      setFilteredStreams(masterStreams.filter(m => String(m.subject_id) === String(abstract.subject_id)));
    }
    if (abstract.stream_id) {
      setFilteredSpecializations(masterSpecializations.filter(s => String(s.stream_id) === String(abstract.stream_id)));
    }

    // Initialize selected reviewers
    const abstractReviewers = abstract.selectedReviewers || [];
    const initialReviewers = [];

    // If they are just IDs, look them up in membersMap
    abstractReviewers.forEach(r => {
      if (typeof r === 'string') {
        const name = membersMap[r];
        if (name) initialReviewers.push({ id: r, full_name: name });
      } else if (r && r.id) {
        initialReviewers.push({ ...r, full_name: r.full_name || r.name });
      }
    });
    setSelectedReviewers(initialReviewers);

    setShowEditModal(true);
  };

  const handleDeleteClick = (abstract) => {
    setAbstractToDelete(abstract);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (abstractToDelete) {
      try {
        await articleService.deleteAbstract(abstractToDelete.id);
        await loadAbstracts();
        setShowDeleteConfirm(false);
        setAbstractToDelete(null);
        showToast('Abstract deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting abstract:', error);
        showToast('Failed to delete abstract. Please try again.', 'error');
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setAbstractToDelete(null);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['.pdf', '.doc', '.docx'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

      if (validTypes.includes(fileExtension)) {
        setEditUploadedFile(file);
        setAbstractToEdit({ ...abstractToEdit, abstractText: '', contentType: 'file' });
      } else {
        showToast('Please upload only PDF or Word documents', 'error');
        e.target.value = '';
      }
    }
  };

  const removeEditFile = () => {
    setEditUploadedFile(null);
    setAbstractToEdit({ ...abstractToEdit, contentType: 'text' });
  };

  const handleAbstractTextChange = (e) => {
    const value = e.target.value;
    if (value.trim()) {
      setEditUploadedFile(null);
      setAbstractToEdit({ ...abstractToEdit, abstractText: value, contentType: 'text' });
    } else {
      setAbstractToEdit({ ...abstractToEdit, abstractText: value });
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    try {
      // Collect data for update
      const guideDetails = {
        name: abstractToEdit.mentorName || abstractToEdit.guideName,
        designation: abstractToEdit.mentorDesignation || abstractToEdit.guideDesignation,
        university: abstractToEdit.mentorUniversity || abstractToEdit.guideUniversity,
        phone: abstractToEdit.mentorPhone || abstractToEdit.guidePhone,
        email: abstractToEdit.mentorEmail || abstractToEdit.guideEmail
      };

      const coAuthors = abstractToEdit.authors.map(a => ({
        name: a.name,
        email: a.email,
        university: a.university
      }));

      let updateData;
      if (editUploadedFile && editUploadedFile instanceof File) {
        // Use FormData if a NEW file is uploaded
        updateData = new FormData();
        updateData.append('title', abstractToEdit.abstractTitle);
        updateData.append('subject_id', abstractToEdit.subject_id);
        updateData.append('stream_id', abstractToEdit.stream_id);
        if (abstractToEdit.specialization_id) updateData.append('specialization_id', abstractToEdit.specialization_id);
        updateData.append('abstract_text', abstractToEdit.abstractText || '');
        updateData.append('keywords', abstractToEdit.keywords);
        updateData.append('submission_date', abstractToEdit.submissionDate);
        updateData.append('university', abstractToEdit.university);
        updateData.append('guide_details', JSON.stringify(guideDetails));
        updateData.append('co_authors', JSON.stringify(coAuthors));
        updateData.append('file', editUploadedFile);
        updateData.append('contentType', 'file');
        updateData.append('suggested_reviewer_ids', JSON.stringify(selectedReviewers.map(r => r.id)));
      } else {
        // Use JSON for other updates
        updateData = {
          title: abstractToEdit.abstractTitle,
          subject_id: abstractToEdit.subject_id,
          stream_id: abstractToEdit.stream_id,
          specialization_id: abstractToEdit.specialization_id || null,
          abstract_text: abstractToEdit.abstractText || '',
          keywords: abstractToEdit.keywords,
          submission_date: abstractToEdit.submissionDate,
          university: abstractToEdit.university,
          guide_details: guideDetails,
          co_authors: coAuthors,
          contentType: abstractToEdit.contentType || 'text',
          suggested_reviewer_ids: selectedReviewers.map(r => r.id)
        };
      }

      if (abstractToEdit.status === 'modification_requested') {
        // If modification was requested, we use the resubmitVersion endpoint
        let resubmitData;
        if (editUploadedFile && editUploadedFile instanceof File) {
          resubmitData = new FormData();
          resubmitData.append('title', abstractToEdit.abstractTitle);
          resubmitData.append('abstract_text', abstractToEdit.abstractText || '');
          resubmitData.append('file', editUploadedFile);
        } else {
          resubmitData = {
            title: abstractToEdit.abstractTitle,
            abstract_text: abstractToEdit.abstractText || ''
          };
        }
        await articleService.resubmitVersion(abstractToEdit.id, resubmitData);
      } else {
        // Otherwise use standard updateAbstract endpoint
        await articleService.updateAbstract(abstractToEdit.id, updateData);
      }

      await loadAbstracts();
      setShowEditModal(false);
      setAbstractToEdit(null);
      setEditUploadedFile(null);
      showToast('Abstract updated successfully', 'success');
    } catch (error) {
      console.error('Error updating abstract:', error);
      showToast(error.message || 'Failed to update abstract. Please try again.', 'error');
    }
  };


  const handleEditChange = (field, value) => {
    setAbstractToEdit(prev => ({ ...prev, [field]: value }));

    // Handle cascading logic for edit modal
    if (field === 'subject_id') {
      const sub = masterSubjects.find(s => s.id === value);
      setAbstractToEdit(prev => ({
        ...prev,
        subject_id: value,
        subject: sub?.name || '',
        stream_id: '',
        major: '',
        specialization_id: '',
        specialization: ''
      }));
      setFilteredStreams(masterStreams.filter(m => String(m.subject_id) === String(value)));
      setFilteredSpecializations([]);
    } else if (field === 'stream_id') {
      const str = masterStreams.find(s => s.id === value);
      setAbstractToEdit(prev => ({
        ...prev,
        stream_id: value,
        major: str?.name || '',
        specialization_id: '',
        specialization: ''
      }));
      setFilteredSpecializations(masterSpecializations.filter(s => String(s.stream_id) === String(value)));
    } else if (field === 'specialization_id') {
      const spec = masterSpecializations.find(s => s.id === value);
      setAbstractToEdit(prev => ({
        ...prev,
        specialization_id: value,
        specialization: spec?.name || ''
      }));
    }
  };

  const handleAuthorChange = (id, field, value) => {
    setAbstractToEdit(prev => {
      const updatedAuthors = prev.authors.map(author =>
        author.id === id ? { ...author, [field]: value } : author
      );
      return { ...prev, authors: updatedAuthors };
    });

    // Trigger global university search if updating searchUniversity
    if (field === 'searchUniversity') {
      if (value.length >= 2) {
        setSearchUniversity(value); // Reuse the global search logic
      } else if (value.length === 0) {
        setUniversityList(masterUniversities);
        setSearchUniversity('');
      }
    }
  };

  const addAuthor = () => {
    const newId = `author-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    setAbstractToEdit(prev => ({
      ...prev,
      authors: [...prev.authors, { id: newId, name: '', email: '', university: '', searchUniversity: '', showUniversityDropdown: false }]
    }));
  };

  const removeAuthor = (id) => {
    if (abstractToEdit.authors.length > 1) {
      const updatedAuthors = abstractToEdit.authors.filter(author => author.id !== id);
      setAbstractToEdit({ ...abstractToEdit, authors: updatedAuthors });
    } else {
      showToast('At least one author is required', 'error');
    }
  };

  const inputClass = "w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all hover:border-gray-300 text-sm";
  const labelClass = "block text-sm font-semibold text-gray-700 mb-2";

  // Reviewer helpers for edit modal
  const isReviewerSelected = (id) => selectedReviewers.some(r => r.id === id);

  const toggleReviewer = (item, type) => {
    const name = item.full_name || item.name || 'Unnamed Member';
    if (isReviewerSelected(item.id)) {
      setSelectedReviewers(prev => prev.filter(r => r.id !== item.id));
    } else {
      setSelectedReviewers(prev => [...prev, { ...item, type, full_name: name }]);
    }
    reviewerInputRef.current?.focus();
  };

  const handleReviewerInputClick = () => {
    setShowReviewerDropdown(prev => !prev);
  };

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Review Comment Modal (for both Acceptance and Rejection) */}
      {showReviewCommentModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full animate-fade-in">
            <div className={`p-6 rounded-t-2xl ${reviewCommentType === 'accepted' ? 'bg-gradient-to-r from-green-500 to-green-600' :
              reviewCommentType === 'rejected' ? 'bg-gradient-to-r from-red-500 to-red-600' :
                'bg-gradient-to-r from-amber-500 to-amber-600'
              }`}>
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">
                    {reviewCommentType === 'accepted' ? 'Acceptance Comments' :
                      reviewCommentType === 'rejected' ? 'Rejection Comments' :
                        'Modification Feedback'}
                  </h2>
                  <p className="text-white/80">
                    Reviewer's feedback for your submission
                  </p>
                </div>
                <button
                  onClick={() => setShowReviewCommentModal(false)}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-all"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Reviewer's Feedback</h3>
                <div className={`border-l-4 p-4 rounded-r-lg ${reviewCommentType === 'accepted' ? 'bg-green-50 border-green-500' :
                  reviewCommentType === 'rejected' ? 'bg-red-50 border-red-500' :
                    'bg-amber-50 border-amber-500'
                  }`}>
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedReviewComment}</p>
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => setShowReviewCommentModal(false)}
                  className={`px-6 py-3 text-white rounded-lg font-semibold hover:shadow-lg transition-all ${reviewCommentType === 'accepted' ? 'bg-gradient-to-r from-green-500 to-green-600' :
                    reviewCommentType === 'rejected' ? 'bg-gradient-to-r from-red-500 to-red-600' :
                      'bg-gradient-to-r from-amber-500 to-amber-600'
                    }`}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && abstractToEdit && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full my-8 max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-primary-blue to-secondary-blue p-6 rounded-t-2xl sticky top-0 z-40">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Edit Abstract</h2>
                  <p className="text-blue-100">Update your abstract details</p>
                </div>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditUploadedFile(null);
                  }}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-all"
                >
                  ✕
                </button>
              </div>
            </div>

            <form onSubmit={handleEditSubmit} className="p-6">
              <div className="space-y-6">
                {/* Academic Information */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Academic Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className={labelClass}>Department</label>
                      <select
                        value={abstractToEdit.subject_id}
                        onChange={(e) => handleEditChange('subject_id', e.target.value)}
                        required={false}
                        className={`${inputClass} bg-white`}
                      >
                        <option value="">Select Department</option>
                        {masterSubjects.map((sub) => (
                          <option key={sub.id} value={sub.id}>{sub.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Stream</label>
                      <select
                        value={abstractToEdit.stream_id}
                        onChange={(e) => handleEditChange('stream_id', e.target.value)}
                        required={false}
                        disabled={!abstractToEdit.subject_id}
                        className={`${inputClass} bg-white disabled:bg-gray-50`}
                      >
                        <option value="">Select Stream</option>
                        {filteredStreams.map((str) => (
                          <option key={str.id} value={str.id}>{str.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Specialization</label>
                      <select
                        value={abstractToEdit.specialization_id}
                        onChange={(e) => handleEditChange('specialization_id', e.target.value)}
                        disabled={!abstractToEdit.stream_id}
                        className={`${inputClass} bg-white disabled:bg-gray-50`}
                      >
                        <option value="">Select Specialization</option>
                        {filteredSpecializations.map((spec) => (
                          <option key={spec.id} value={spec.id}>{spec.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className={`relative ${showUniversityDropdown ? 'z-30' : ''}`}>
                      <label className={labelClass}>University</label>
                      <input
                        type="text"
                        placeholder="Search & Select University"
                        value={searchUniversity}
                        onChange={(e) => {
                          setSearchUniversity(e.target.value);
                          handleEditChange('university', '');
                          setShowUniversityDropdown(true);
                        }}
                        onFocus={() => setShowUniversityDropdown(true)}
                        onBlur={() => setTimeout(() => setShowUniversityDropdown(false), 200)}
                        className={`${inputClass} bg-white`}
                        required={false}
                      />
                      {showUniversityDropdown && (
                        <div className="absolute top-full left-0 z-30 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                          {universityList.length > 0 ? (
                            universityList.map((uni, idx) => (
                              <div
                                key={idx}
                                className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                                onMouseDown={() => {
                                  const name = uni.name || uni;
                                  setAbstractToEdit({ ...abstractToEdit, university: name });
                                  setSearchUniversity(name);
                                  setShowUniversityDropdown(false);
                                }}
                              >
                                {uni.name || uni}
                              </div>
                            ))
                          ) : (
                            <div className="px-3 py-2 text-gray-400 text-sm">No results</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Abstract Details */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Abstract Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Abstract Title</label>
                      <input
                        type="text"
                        value={abstractToEdit.abstractTitle}
                        onChange={(e) => handleEditChange('abstractTitle', e.target.value)}
                        required={false}
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Submission Date</label>
                      <input
                        type="date"
                        value={abstractToEdit.submissionDate}
                        onChange={(e) => handleEditChange('submissionDate', e.target.value)}
                        required={false}
                        className={inputClass}
                      />
                    </div>
                  </div>
                </div>

                {/* Authors Information */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Authors Information</h3>
                  {abstractToEdit.authors && abstractToEdit.authors.map((author, index) => (
                    <div key={author.id} className="mb-4 p-4 bg-green-50/50 rounded-xl border-l-4 border-green-500">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-sm font-bold text-gray-800">Author {index + 1}</h4>
                        {abstractToEdit.authors.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeAuthor(author.id)}
                            className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                          >
                            <FaTrash className="text-sm" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className={labelClass}>Author Name</label>
                          <input
                            type="text"
                            value={author.name}
                            onChange={(e) => handleAuthorChange(author.id, 'name', e.target.value)}
                            required={false}
                            className={inputClass}
                            placeholder="Full name"
                          />
                        </div>

                        <div>
                          <label className={labelClass}>Author Email</label>
                          <input
                            type="email"
                            value={author.email}
                            onChange={(e) => handleAuthorChange(author.id, 'email', e.target.value)}
                            required={false}
                            className={inputClass}
                            placeholder="email@example.com"
                          />
                        </div>

                        <div className={`relative ${author.showUniversityDropdown ? 'z-30' : ''}`}>
                          <label className={labelClass}>Author University</label>
                          <input
                            type="text"
                            placeholder="Search & Select University"
                            value={author.university || author.searchUniversity || ''}
                            onChange={(e) => {
                              handleAuthorChange(author.id, 'searchUniversity', e.target.value);
                              handleAuthorChange(author.id, 'university', '');
                              handleAuthorChange(author.id, 'showUniversityDropdown', true);
                            }}
                            onFocus={() => handleAuthorChange(author.id, 'showUniversityDropdown', true)}
                            onBlur={() => setTimeout(() => handleAuthorChange(author.id, 'showUniversityDropdown', false), 200)}
                            className={`${inputClass} bg-white`}
                            required={false}
                          />
                          {author.showUniversityDropdown && (
                            <div className="absolute top-full left-0 z-30 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                              {universityList.length > 0 ? (
                                universityList.map((uni, idx) => (
                                  <div
                                    key={idx}
                                    className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                                    onMouseDown={() => {
                                      const name = uni.name || uni;
                                      handleAuthorChange(author.id, 'university', name);
                                      handleAuthorChange(author.id, 'searchUniversity', name);
                                      handleAuthorChange(author.id, 'showUniversityDropdown', false);
                                    }}
                                  >
                                    {uni.name || uni}
                                  </div>
                                ))
                              ) : (
                                <div className="px-3 py-2 text-gray-400 text-sm">No results</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addAuthor}
                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all shadow-md font-semibold"
                  >
                    <FaPlus />
                    Add Another Author
                  </button>
                </div>

                {/* Mentor Information */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Mentor Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div>
                      <label className={labelClass}>Mentor Name</label>
                      <input
                        type="text"
                        value={abstractToEdit.mentorName || abstractToEdit.guideName || ''}
                        onChange={(e) => handleEditChange('mentorName', e.target.value)}
                        required={false}
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Designation</label>
                      <input
                        type="text"
                        value={abstractToEdit.mentorDesignation || abstractToEdit.guideDesignation || ''}
                        onChange={(e) => handleEditChange('mentorDesignation', e.target.value)}
                        required={false}
                        className={inputClass}
                      />
                    </div>

                    <div className={`relative ${showMentorUniversityDropdown ? 'z-30' : ''}`}>
                      <label className={labelClass}>Mentor University</label>
                      <input
                        type="text"
                        placeholder="Search & Select University"
                        value={searchMentorUniversity}
                        onChange={(e) => {
                          setSearchMentorUniversity(e.target.value);
                          handleEditChange('mentorUniversity', '');
                          setShowMentorUniversityDropdown(true);
                        }}
                        onFocus={() => setShowMentorUniversityDropdown(true)}
                        onBlur={() => setTimeout(() => setShowMentorUniversityDropdown(false), 200)}
                        className={`${inputClass} bg-white`}
                        required={false}
                      />
                      {showMentorUniversityDropdown && (
                        <div className="absolute top-full left-0 z-30 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                          {universityList.length > 0 ? (
                            universityList.map((uni, idx) => (
                              <div
                                key={idx}
                                className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                                onMouseDown={() => {
                                  const name = uni.name || uni;
                                  setAbstractToEdit({ ...abstractToEdit, mentorUniversity: name });
                                  setSearchMentorUniversity(name);
                                  setShowMentorUniversityDropdown(false);
                                }}
                              >
                                {uni.name || uni}
                              </div>
                            ))
                          ) : (
                            <div className="px-3 py-2 text-gray-400 text-sm">No results</div>
                          )}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className={labelClass}>Mentor Phone</label>
                      <input
                        type="tel"
                        value={abstractToEdit.mentorPhone || abstractToEdit.guidePhone || ''}
                        onChange={(e) => handleEditChange('mentorPhone', e.target.value)}
                        required={false}
                        maxLength="10"
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Mentor Email</label>
                      <input
                        type="email"
                        value={abstractToEdit.mentorEmail || abstractToEdit.guideEmail || ''}
                        onChange={(e) => handleEditChange('mentorEmail', e.target.value)}
                        required={false}
                        className={inputClass}
                      />
                    </div>
                  </div>
                </div>

                {/* Abstract Content */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Abstract Content</h3>

                  {!editUploadedFile && (
                    <div className="mb-6">
                      <label className={labelClass}>Abstract Text</label>
                      <textarea
                        value={abstractToEdit.abstractText || ''}
                        onChange={handleAbstractTextChange}
                        rows="12"
                        className={inputClass}
                        placeholder="Type your abstract content here..."
                      ></textarea>
                      <p className="text-xs text-gray-500 mt-2">Or upload a document below</p>
                    </div>
                  )}

                  {!abstractToEdit.abstractText && (
                    <div className="mb-6">
                      <label className={labelClass}>Upload Document (PDF or Word)</label>
                      <div className="relative">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileUpload}
                          className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-primary-blue hover:file:bg-blue-100 cursor-pointer"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">Supported formats: PDF, DOC, DOCX</p>
                    </div>
                  )}

                  {editUploadedFile && (
                    <div className="mb-6 p-4 bg-green-50 rounded-lg border-2 border-green-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                            <FaFileUpload className="text-white text-xl" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{editUploadedFile.name}</p>
                            <p className="text-sm text-gray-600">
                              {editUploadedFile.size ? (editUploadedFile.size / 1024).toFixed(2) + ' KB' : 'File'}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={removeEditFile}
                          className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                          title="Remove file"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="mt-4">
                    <label className={labelClass}>Keywords</label>
                    <input
                      type="text"
                      value={abstractToEdit.keywords}
                      onChange={(e) => handleEditChange('keywords', e.target.value)}
                      required={false}
                      className={inputClass}
                      placeholder="Separate keywords with commas"
                    />
                  </div>

                  {/* Reviewer Selection */}
                  <div className="mt-8 pt-6 border-t border-gray-100">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold">R</div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">Select Reviewers</h3>
                        <p className="text-gray-500 text-sm">Update your suggested reviewers (Optional)</p>
                      </div>
                    </div>

                    {/* Selected Reviewers Chips */}
                    {selectedReviewers.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {selectedReviewers.map((reviewer) => (
                          <div
                            key={reviewer.id}
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-indigo-100 to-blue-100 text-indigo-800 rounded-full text-sm font-medium border border-indigo-200 shadow-sm"
                          >
                            <FaUserCheck className="text-indigo-600 text-xs" />
                            <span>{reviewer.full_name}</span>
                            <button
                              type="button"
                              onClick={() => setSelectedReviewers(prev => prev.filter(r => r.id !== reviewer.id))}
                              className="ml-1 w-4 h-4 flex items-center justify-center bg-indigo-300 hover:bg-red-400 text-white rounded-full transition-colors"
                            >
                              <FaTimes className="text-[8px]" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="relative" ref={reviewerDropdownRef}>
                      <div className="relative">
                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm pointer-events-none" />
                        <input
                          ref={reviewerInputRef}
                          type="text"
                          placeholder="Search & suggested reviewers…"
                          value={reviewerSearch}
                          onChange={(e) => {
                            setReviewerSearch(e.target.value);
                            setShowReviewerDropdown(true);
                          }}
                          onClick={handleReviewerInputClick}
                          className={`${inputClass} pl-10 pr-10 bg-white`}
                        />
                        <FaChevronDown
                          className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm transition-transform duration-200 pointer-events-none ${showReviewerDropdown ? 'rotate-180' : ''}`}
                        />
                      </div>

                      {showReviewerDropdown && (
                        <div className="absolute top-full left-0 z-30 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-60 overflow-y-auto">
                          {(() => {
                            const search = (reviewerSearch || '').toLowerCase();
                            const isMatch = (item) => {
                              const name = (item.full_name || item.name || '').toLowerCase();
                              return search === '' || name.includes(search);
                            };

                            const allDisplayMembers = [
                              ...allMembers.reviewers.map(m => ({ ...m, roleLabel: 'Reviewer', type: 'reviewer' })),
                              ...allMembers.editorial_board.map(m => ({ ...m, roleLabel: 'EB Member', type: 'editorial_board' })),
                              ...allMembers.sub_eb_members.map(m => ({ ...m, roleLabel: 'Sub-EB Member', type: 'sub_eb' }))
                            ].filter((item, index, self) =>
                              index === self.findIndex((t) => t.id === item.id) && isMatch(item)
                            );

                            if (allDisplayMembers.length === 0) {
                              return <div className="px-4 py-4 text-center text-gray-400 text-sm">No results</div>;
                            }

                            return (
                              <div className="py-1">
                                {allDisplayMembers.map((item) => {
                                  const name = item.full_name || item.name || 'Unnamed Member';
                                  const selected = isReviewerSelected(item.id);
                                  return (
                                    <div
                                      key={item.id}
                                      className={`px-4 py-2 cursor-pointer transition-colors flex items-center gap-3 border-b border-gray-50 last:border-0 ${selected ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        toggleReviewer(item, item.type);
                                      }}
                                    >
                                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${selected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'}`}>
                                        {selected && <FaCheck className="text-white text-[9px]" />}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold truncate text-gray-800">{name}</p>
                                        <div className="flex items-center gap-2">
                                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-tighter border ${item.type === 'editorial_board'
                                            ? 'bg-amber-100 text-amber-700 border-amber-200'
                                            : item.type === 'sub_eb'
                                              ? 'bg-purple-100 text-purple-700 border-purple-200'
                                              : 'bg-blue-100 text-blue-700 border-blue-200'
                                            }`}>
                                            {item.roleLabel}
                                          </span>
                                          {item.university && <p className="text-[10px] text-gray-500 truncate">{item.university}</p>}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                                <div className="p-2 border-t border-gray-100 sticky bottom-0 bg-white">
                                  <button
                                    type="button"
                                    onClick={() => setShowReviewerDropdown(false)}
                                    className="w-full py-2 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700 transition-colors shadow-sm"
                                  >
                                    Done
                                  </button>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditUploadedFile(null);
                  }}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-primary-blue to-secondary-blue text-white rounded-lg font-semibold hover:shadow-lg transition-all"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-fade-in">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaTrash className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Delete Abstract</h3>
              <p className="text-gray-600">Are you sure you want to delete this abstract? This action cannot be undone.</p>
              {abstractToDelete && (
                <p className="text-sm text-gray-500 mt-2 font-semibold">"{abstractToDelete.abstractTitle}"</p>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={cancelDelete}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg font-semibold hover:from-red-600 hover:to-red-700 transition-all"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-indigo-50 py-6">
        <div className="max-w mx-auto px-4">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-blue to-secondary-blue rounded-2xl shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">My Abstracts</h1>
                <p className="text-purple-100">View and track your submitted abstracts</p>
              </div>
              <div className="hidden md:block">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                  <FaFileContract className="text-3xl text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Abstracts List */}
          {loading ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <FaSpinner className="text-6xl text-blue-400 mx-auto mb-4 animate-spin" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Loading Abstracts...</h3>
              <p className="text-gray-600">Fetching your submitted abstracts from the server.</p>
            </div>
          ) : abstracts.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <FaFileContract className="text-6xl text-gray-300 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No Abstracts Yet</h3>
              <p className="text-gray-600">You haven't submitted any abstracts for review.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {abstracts.map((abstract) => {
                const statusConfig = getStatusConfig(abstract.status);
                const StatusIcon = statusConfig.icon;
                const isLocked = (abstract.status !== 'submitted' && abstract.status !== 'modification_requested') || (!!abstract.assignedTo && abstract.status === 'submitted');
                const canEdit = !isLocked || abstract.status === 'modification_requested';
                const canDelete = !isLocked && abstract.status !== 'modification_requested';
                const hasReviewComment = (['accepted', 'rejected', 'modification_requested'].includes(abstract.status)) && abstract.reviewComment && abstract.reviewComment.trim();

                return (
                  <div
                    key={abstract.id}
                    className={`bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all border-l-4 ${statusConfig.borderColor}`}
                  >
                    <div className="p-6">
                      {/* Status and Dates Timeline - Status now at top-right corner */}
                      <div className="flex flex-wrap justify-between items-start mb-6">
                        {/* Left: All Dates */}
                        <div className="flex flex-wrap items-center gap-4">
                          {/* All Dates in Same Line */}
                          <div className="flex flex-wrap gap-4 text-xm text-gray-1000">
                            {abstract.submittedAt && (
                              <div className="flex items-center gap-1">
                                <FaCalendarAlt />
                                <span>Submitted: {formatDate(abstract.submittedAt)}</span>
                              </div>
                            )}
                            {abstract.inQueueAt && (
                              <div className="flex items-center gap-1">
                                <FaCalendarAlt />
                                <span>In Queue: {formatDate(abstract.inQueueAt)}</span>
                              </div>
                            )}
                            {abstract.assignedReviewerDate && !abstract.isReviewerInvitationRejected && (
                              <div className="flex items-center gap-1">
                                <FaCalendarAlt className="text-indigo-500" />
                                <span className="text-indigo-700 font-medium">Assigned Reviewer Date: {formatDate(abstract.assignedReviewerDate)}</span>
                              </div>
                            )}
                            {(abstract.reviewerResponseDate || abstract.inReviewAt) && !abstract.isReviewerInvitationRejected && (
                              <div className="flex items-center gap-1">
                                <FaCalendarAlt className={abstract.reviewerResponseDate ? "text-emerald-500" : ""} />
                                <span className={abstract.reviewerResponseDate ? "text-emerald-700 font-medium" : ""}>
                                  In Review: {formatDate(abstract.reviewerResponseDate || abstract.inReviewAt)}
                                </span>
                              </div>
                            )}
                            {abstract.modificationRequestedAt && (
                              <div className="flex items-center gap-1">
                                <FaCalendarAlt className="text-amber-500" />
                                <span className="text-amber-700 font-medium">Modification Requested: {formatDate(abstract.modificationRequestedAt)}</span>
                              </div>
                            )}
                            {abstract.acceptedAt && (
                              <div className="flex items-center gap-1">
                                <FaCalendarAlt className="text-emerald-500" />
                                <span className="text-emerald-700 font-medium">Accepted: {formatDate(abstract.acceptedAt)}</span>
                              </div>
                            )}
                            {abstract.rejectedAt && (
                              <div className="flex items-center gap-1">
                                <FaCalendarAlt className="text-red-500" />
                                <span className="text-red-700 font-medium">Rejected: {formatDate(abstract.rejectedAt)}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Right: Status Badge with Eye icon for accepted/rejected status with comments */}
                        <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${statusConfig.bgColor} ${statusConfig.textColor} text-sm font-semibold`}>
                          <StatusIcon className={abstract.status === 'in_review' ? 'animate-spin' : ''} />
                          {statusConfig.label}

                          {/* Eye icon for accepted/rejected status with comments */}
                          {hasReviewComment && (
                            <button
                              onClick={() => handleReviewCommentClick(abstract)}
                              className={`ml-2 transition-colors ${abstract.status === 'accepted' ? 'text-green-600 hover:text-green-800' :
                                abstract.status === 'rejected' ? 'text-red-600 hover:text-red-800' :
                                  'text-amber-600 hover:text-amber-800'
                                }`}
                              title={`View ${abstract.status === 'accepted' ? 'acceptance' : abstract.status === 'rejected' ? 'rejection' : 'modification'} feedback`}
                            >
                              <FaEye />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Main Info Row - Flexible layout that auto-adjusts to fill space */}
                      <div className="flex flex-wrap gap-4 mb-4">
                        {/* 1. Abstract Title - Takes more space */}
                        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-3 rounded-lg border-2 border-indigo-200 flex-[2_1_250px] min-w-[250px]">
                          <p className="text-xs text-gray-500 mb-1 font-semibold">Abstract Title</p>
                          <p className="font-semibold text-gray-900 text-sm line-clamp-2" title={abstract.abstractTitle}>
                            {abstract.abstractTitle}
                          </p>
                        </div>

                        {/* 2. Department */}
                        <div className="bg-gray-50 p-3 rounded-lg flex-[1_1_150px] min-w-[150px]">
                          <p className="text-xs text-gray-500 mb-1 font-semibold">Department</p>
                          <p className="font-semibold text-gray-900 text-sm">{abstract.subject}</p>
                        </div>

                        {/* 3. Stream */}
                        <div className="bg-gray-50 p-3 rounded-lg flex-[1_1_150px] min-w-[150px]">
                          <p className="text-xs text-gray-500 mb-1 font-semibold">Stream</p>
                          <p className="font-semibold text-gray-900 text-sm">{abstract.major}</p>
                        </div>

                        {/* 4. Specialization - Only show if exists and not empty */}
                        {abstract.specialization && abstract.specialization.trim() && (
                          <div className="bg-gray-50 p-3 rounded-lg flex-[1_1_150px] min-w-[150px]">
                            <p className="text-xs text-gray-500 mb-1 font-semibold">Specialization</p>
                            <p className="font-semibold text-gray-900 text-sm">{abstract.specialization}</p>
                          </div>
                        )}

                        {/* 5. Authors */}
                        <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-3 rounded-lg border-2 border-teal-200 flex-[1_1_180px] min-w-[180px]">
                          <p className="text-xs text-gray-500 mb-1 font-semibold">Authors</p>
                          <div className="flex items-center gap-2">
                            <FaUser className="text-teal-500 text-sm" />
                            <p className="font-semibold text-gray-900 text-sm line-clamp-2" title={abstract.authors && abstract.authors.length > 0 ? abstract.authors.map(a => a.name).join(', ') : 'N/A'}>
                              {abstract.authors && abstract.authors.length > 0
                                ? abstract.authors.map(a => a.name).join(', ')
                                : 'N/A'}
                            </p>
                          </div>
                        </div>

                        {/* 6. Content Type */}
                        {abstract.uploadedFile ? (
                          <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-3 rounded-lg border-2 border-purple-200 flex-[1_1_180px] min-w-[180px]">
                            <p className="text-xs text-gray-500 mb-2 font-semibold">Content Type</p>
                            <div className="flex items-center gap-2">
                              {getFileIcon(abstract.uploadedFile.name)}
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold text-gray-900 truncate" title={abstract.uploadedFile.name}>
                                  {abstract.uploadedFile.name}
                                </p>
                              </div>
                            </div>
                          </div>
                        ) : abstract.abstractText && (
                          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-3 rounded-lg border-2 border-blue-200 flex-[1_1_150px] min-w-[150px]">
                            <p className="text-xs text-gray-500 mb-2 font-semibold">Content Type</p>
                            <div className="flex items-center gap-2">
                              <FaFileAlt className="text-blue-500 text-lg" />
                              <p className="text-sm font-semibold text-gray-900">Text</p>
                            </div>
                          </div>
                        )}

                        {/* 7. Assigned Reviewer - ONLY if assigned */}
                        {abstract.assignedTo && (
                          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-3 rounded-lg border-2 border-amber-200 flex-[1_1_180px] min-w-[180px]">
                            <p className="text-xs text-gray-500 mb-1 font-semibold">Assigned Reviewer</p>
                            <div className="flex items-center gap-2">
                              <FaUserTie className="text-amber-600 text-sm" />
                              <p className="font-semibold text-gray-900 text-sm line-clamp-2"
                                title={typeof abstract.assignedTo === 'string'
                                  ? (membersMap[abstract.assignedTo] || 'Reviewer')
                                  : (abstract.assignedTo.full_name || abstract.assignedTo.name || abstract.assignedTo.professorName || 'Reviewer')}>
                                {typeof abstract.assignedTo === 'string'
                                  ? (membersMap[abstract.assignedTo] || 'Assigned Reviewer')
                                  : (abstract.assignedTo.full_name || abstract.assignedTo.name || abstract.assignedTo.professorName || 'Reviewer')}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* 8. Suggested Reviewers */}
                        {abstract.selectedReviewers && abstract.selectedReviewers.length > 0 && (
                          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-3 rounded-lg border-2 border-blue-200 flex-[1_1_200px] min-w-[200px]">
                            <p className="text-xs text-gray-500 mb-1 font-semibold">Suggested Reviewers</p>
                            <div className="flex items-center gap-2">
                              <FaUserCheck className="text-blue-600 text-sm" />
                              <p className="font-semibold text-gray-900 text-sm line-clamp-2" title={abstract.selectedReviewers.map(r => {
                                if (typeof r === 'string') return membersMap[r] || r;
                                return r.full_name || r.name || 'Unknown';
                              }).join(', ')}>
                                {abstract.selectedReviewers.map(r => {
                                  if (typeof r === 'string') return membersMap[r] || r;
                                  return r.full_name || r.name || 'Unknown';
                                }).join(', ')}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex justify-end gap-3">
                        {canEdit && (
                          <button
                            onClick={() => handleEditClick(abstract)}
                            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-blue to-secondary-blue text-white rounded-lg hover:shadow-lg transition-all font-semibold"
                          >
                            <FaEdit />
                            Edit
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => handleDeleteClick(abstract)}
                            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:shadow-lg transition-all font-semibold"
                          >
                            <FaTrash />
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MyAbstract;